import React, { FC, useEffect, useMemo } from 'react'
import Chart from 'react-apexcharts'

import { IChatsWalletResponse } from '@/core/services/Wallets/WalletInterface'
import { chatsSelector } from '@/core/services/Wallets/WalletSelector'
import { getChatsWalletThunk } from '@/core/services/Wallets/WalletSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'

import './WalletChart.scss'

interface IWalletChartProps {
  className?: string
  code: string
}

interface ChartDataPoint {
  x: number
  y: number
}

const cnWalletChart = cn('WalletChart')

export const WalletChart: FC<IWalletChartProps> = ({ className, code }) => {
  const dispatch = useAppDispatch()
  const chats = useAppSelector(chatsSelector)

  // Загружаем данные при монтировании компонента
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(
          getChatsWalletThunk({ code: code, params: 'w' }),
        ).unwrap()
      } catch (error) {
        console.log('Ошибка загрузки данных:', error)
      }
    }

    loadData()
  }, [dispatch, code])

  const processChartData = (
    chatsData: IChatsWalletResponse | null,
  ): ChartDataPoint[] => {
    if (!chatsData?.chars || !Array.isArray(chatsData.chars)) {
      return []
    }

    return chatsData.chars
      .map((item: { time: string; value: string }) => {
        const [datePart, timePart] = item.time.split(' ')
        const [day, month, year] = datePart.split('.')
        const [hours, minutes] = timePart.split(':')

        const date = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hours),
          parseInt(minutes),
        )

        return {
          x: date.getTime(),
          y: +parseFloat(item.value || '0'),
        }
      })
      .filter(
        (item: ChartDataPoint) =>
          !isNaN(item.y) && item.y > 0 && !isNaN(item.x),
      )
  }

  // Получаем обработанные данные для графика
  const chartData = useMemo(() => processChartData(chats), [chats])

  // Вычисляем диапазон дат для brush chart
  // const dateRange = useMemo(() => {
  //   if (chartData.length === 0) {
  //     return { min: Date.now() - 7 * 24 * 60 * 60 * 1000, max: Date.now() }
  //   }

  //   const timestamps = chartData
  //     .map((item: ChartDataPoint) => item.x)
  //     .sort((a: number, b: number) => a - b)
  //   return {
  //     min: timestamps[0],
  //     max: timestamps[timestamps.length - 1],
  //   }
  // }, [chartData])

  // Основной график
  const mainChartOptions: ApexCharts.ApexOptions = {
    chart: {
      id: 'main-chart',
      type: 'line',
      toolbar: {
        show: false,
      },
      background: 'transparent',
      zoom: {
        enabled: false,
      },
    },
    colors: ['#30DAAD'],
    stroke: {
      curve: 'smooth',
      width: 1.3,
    },
    markers: {
      size: 0,
    },
    grid: {
      show: false,
    },
    legend: {
      show: false,
    },
    xaxis: {
      type: 'datetime',
      labels: {
        style: {
          colors: '#A9A9A9',
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      opposite: true,
      labels: {
        style: {
          colors: '#A9A9A9',
        },
        formatter: (value: number) => value.toFixed(0), // Убираем лишние нули
      },
    },
    tooltip: {
      enabled: true,
      theme: 'dark',
      x: {
        format: 'HH:mm:ss',
      },
      marker: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
  }

  // Brush Chart (график для скроллинга)
  // const brushChartOptions: ApexCharts.ApexOptions = {
  //   chart: {
  //     id: 'brush-chart',
  //     type: 'bar',
  //     background: 'transparent',
  //     brush: {
  //       target: 'main-chart',
  //       enabled: true,
  //     },
  //     selection: {
  //       enabled: true,
  //       xaxis: {
  //         min: dateRange.min,
  //         max: dateRange.max,
  //       },
  //     },
  //   },
  //   colors: ['rgba(255, 255, 255, 0.10)'],
  //   plotOptions: {
  //     bar: {
  //       columnWidth: '6px',
  //     },
  //   },
  //   xaxis: {
  //     type: 'datetime',
  //     labels: {
  //       show: false,
  //     },
  //     axisBorder: {
  //       show: false,
  //     },
  //     axisTicks: {
  //       show: false,
  //     },
  //   },
  //   yaxis: {
  //     show: false,
  //   },
  //   grid: {
  //     show: false,
  //   },
  //   tooltip: {
  //     enabled: false,
  //   },
  // }

  const chartSeries = [
    {
      name: 'USD', //code.toUpperCase(),
      data: chartData,
    },
  ]

  return (
    <div className={cnWalletChart(null, [className])}>
      <Chart
        options={mainChartOptions}
        series={chartSeries}
        type='line'
        height={350}
        width='100%'
      />
      {/* <Chart
        options={brushChartOptions}
        series={chartSeries}
        type='bar'
        height={100}
        width='100%'
      /> */}
    </div>
  )
}
